/** Dikkat şablonları: tarama, farkı bul, farklı olan, kelime bulmaca, yol takibi */

import { Rng } from '../../prng'
import { SEKIL_ADI } from '../../render/shapes'
import {
  ARROWS,
  LETTERS,
  SHAPES_CONFUSABLE,
  SHAPES_SIMPLE,
  WORDS_TR,
  gradeList,
  gradeSet,
  trUpper,
} from '../helpers'
import { EXERCISE_VERSION, scale, clampLevel } from '../types'
import type { CellNode, ExerciseTemplate } from '../types'

/* ================================================================== *
 * 1. Hedef tarama
 *
 * Izgarada belirli bir sembolün hepsini bul. Zorluk üç yerden gelir:
 * ızgara büyür, hedef seyrelir ve ÇELDİRİCİ HEDEFE BENZER HALE GELİR.
 * Üçüncüsü en önemlisi — sadece ızgarayı büyütmek görevi uzatır,
 * zorlaştırmaz.
 * ================================================================== */
export const scanTarget: ExerciseTemplate = {
  code: 'scan_target',
  title: 'Hedefi bul',
  domains: ['selective_attention', 'processing_speed'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return {
      cols: scale(l, 6, 14),
      rows: scale(l, 5, 12),
      targetRatio: 0.18 - (l - 1) * 0.01,
      confusable: l >= 5,
    }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const cols = p['cols'] as number
    const rows = p['rows'] as number
    const n = cols * rows
    const pool = p['confusable'] ? SHAPES_CONFUSABLE : SHAPES_SIMPLE
    const target = rng.pick(pool)
    const distractors = pool.filter((s) => s !== target)

    const cells: CellNode[] = []
    const answer: string[] = []
    const want = Math.max(3, Math.round(n * (p['targetRatio'] as number)))
    const targetSlots = new Set(rng.sample(Array.from({ length: n }, (_, i) => i), want))

    for (let i = 0; i < n; i++) {
      const isTarget = targetSlots.has(i)
      const id = 'c' + i
      cells.push({ shape: isTarget ? target : rng.pick(distractors), id })
      if (isTarget) answer.push(id)
    }

    return {
      code: 'scan_target',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: `Aşağıdaki tabloda ${SEKIL_ADI[target]} şeklinin hepsini işaretle.`,
      render: [
        /**
         * Hedef ŞEKİL olarak gösteriliyor, adı yazılarak değil.
         *
         * Yönergedeki "daire" sözcüğü uygulayıcı sesli okusun diye var;
         * çocuğun okuması gerekmiyor ve 6 yaşındaki okuyamıyor. Tek
         * hücrelik bir ızgara, hedefi hem ekranda hem A4'te ızgaradaki
         * hücrelerle BİREBİR aynı yoldan çiziyor — "örnekteki şekil
         * tablodakinden biraz farklı duruyor" sorusu doğmuyor.
         */
        { t: 'text', value: 'Hedef:', size: 'sm' },
        { t: 'grid', cols: 1, rows: 1, cells: [{ shape: target, marked: true }], border: true },
        { t: 'grid', cols, rows, cells, border: false },
      ],
      solution: answer,
      grading: 'auto',
      estimatedSeconds: Math.round(n * 0.6),
      itemCount: answer.length,
    }
  },
  grade(instance, answer) {
    return gradeSet(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 2. Farkı bul
 *
 * İki ızgara yan yana; birkaç hücre farklı. Kağıt kanalda da çalışsın
 * diye iki ayrı ızgara düğümü olarak veriliyor.
 * ================================================================== */
export const spotDifference: ExerciseTemplate = {
  code: 'spot_difference',
  title: 'Farkı bul',
  domains: ['selective_attention'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { size: scale(l, 4, 8), differences: scale(l, 2, 6), confusable: l >= 6 }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const size = p['size'] as number
    const n = size * size
    const pool = p['confusable'] ? SHAPES_CONFUSABLE : SHAPES_SIMPLE

    // Sol tablo SADECE REFERANS: kimliği yok, dolayısıyla işaretlenemez.
    // Kimliği olduğunda ekranda tıklanabiliyor ama o tıklamalar
    // puanlamaya girmiyordu — çocuk doğru farkı solda işaretleyip
    // puan alamazdı.
    const left: CellNode[] = Array.from({ length: n }, () => ({ shape: rng.pick(pool) }))
    const right = left.map((c, i) => ({ shape: c.shape, id: 'r' + i }))

    const diffCount = p['differences'] as number
    const diffs = rng.sample(Array.from({ length: n }, (_, i) => i), diffCount)
    for (const i of diffs) {
      const others = pool.filter((s) => s !== left[i]!.shape)
      right[i]!.shape = rng.pick(others)
    }

    return {
      code: 'spot_difference',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: `İki tablo arasında ${diffCount} fark var. Sağdaki tabloda farklı olan kutuları işaretle.`,
      render: [
        { t: 'text', value: 'Soldaki', size: 'sm' },
        { t: 'grid', cols: size, rows: size, cells: left, border: true },
        { t: 'text', value: 'Sağdaki', size: 'sm' },
        { t: 'grid', cols: size, rows: size, cells: right, border: true },
      ],
      solution: diffs.map((i) => 'r' + i),
      grading: 'auto',
      estimatedSeconds: 30 + diffCount * 15,
      itemCount: diffCount,
    }
  },
  grade(instance, answer) {
    return gradeSet(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 3. Farklı olanı bul
 *
 * Her satırda bir öğe kuralı bozar. Düşük seviyede şekil farkı,
 * yüksek seviyede yön farkı (daha ince ayrım).
 * ================================================================== */
export const oddOneOut: ExerciseTemplate = {
  code: 'odd_one_out',
  title: 'Farklı olanı bul',
  domains: ['selective_attention', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { rows: scale(l, 4, 10), perRow: scale(l, 4, 8), useArrows: l >= 4 }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const rows = p['rows'] as number
    const perRow = p['perRow'] as number
    const pool = p['useArrows'] ? ARROWS : SHAPES_SIMPLE

    const cells: CellNode[] = []
    const solution: string[] = []
    for (let r = 0; r < rows; r++) {
      const base = rng.pick(pool)
      const odd = rng.pick(pool.filter((s) => s !== base))
      const oddAt = rng.int(0, perRow - 1)
      for (let c = 0; c < perRow; c++) {
        const id = `r${r}c${c}`
        cells.push({ shape: c === oddAt ? odd : base, id })
        if (c === oddAt) solution.push(id)
      }
    }

    return {
      code: 'odd_one_out',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Her satırda diğerlerinden farklı olan bir tane var. Onu işaretle.',
      render: [{ t: 'grid', cols: perRow, rows, cells, border: false, gap: 2 }],
      solution,
      grading: 'auto',
      estimatedSeconds: rows * 8,
      itemCount: rows,
    }
  },
  grade(instance, answer) {
    return gradeSet(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 4. Kelime bulmaca
 *
 * Kelimeler ızgaraya yatay/dikey/çapraz yerleştirilir. Yerleştirme
 * başarısız olursa kelime atlanır ve çözüm listesi buna göre küçülür —
 * yani çözümde var ama ızgarada olmayan bir kelime ASLA oluşmaz.
 * ================================================================== */
export const wordSearch: ExerciseTemplate = {
  code: 'word_search',
  title: 'Kelime bulmaca',
  domains: ['selective_attention', 'working_memory_verbal'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { size: scale(l, 8, 14), words: scale(l, 4, 9), diagonal: l >= 5, reversed: l >= 8 }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const size = p['size'] as number
    const grid: (string | null)[] = new Array(size * size).fill(null)

    const dirs: [number, number][] = [
      [1, 0],
      [0, 1],
    ]
    if (p['diagonal']) dirs.push([1, 1], [1, -1])
    if (p['reversed']) dirs.push([-1, 0], [0, -1])

    const chosen = rng.sample(WORDS_TR, Math.min(p['words'] as number, WORDS_TR.length))
    const placed: string[] = []
    /**
     * Her kelimenin hangi hücrelere yerleştiği.
     *
     * Yalnızca kelime listesini çözüm olarak tutmak yetmiyordu: ekranda
     * çocuk HÜCRE işaretliyor, kelime yazmıyor. Çözüm hücre düzeyinde
     * olmazsa doğru işaretlenmiş bir sayfa "0 doğru" alıyor.
     */
    const placements: { word: string; cells: string[] }[] = []

    for (const word of chosen) {
      // Türkçe büyük harf: 'incir' → 'İNCİR'. Düz toUpperCase 'INCIR' verir
      // ve ızgaradaki harf kelimeyle tutmaz.
      const w = trUpper(word)
      let done = false
      for (let attempt = 0; attempt < 120 && !done; attempt++) {
        const [dx, dy] = rng.pick(dirs)
        const r = rng.int(0, size - 1)
        const c = rng.int(0, size - 1)
        const endR = r + dy * (w.length - 1)
        const endC = c + dx * (w.length - 1)
        if (endR < 0 || endR >= size || endC < 0 || endC >= size) continue

        let fits = true
        for (let i = 0; i < w.length; i++) {
          const cell = grid[(r + dy * i) * size + (c + dx * i)]
          if (cell !== null && cell !== w[i]) {
            fits = false
            break
          }
        }
        if (!fits) continue
        const cells: string[] = []
        for (let i = 0; i < w.length; i++) {
          const idx = (r + dy * i) * size + (c + dx * i)
          grid[idx] = w[i]!
          cells.push('w' + idx)
        }
        placed.push(word)
        placements.push({ word, cells })
        done = true
      }
    }

    const cells: CellNode[] = grid.map((g, i) => ({
      glyph: g ?? rng.pick(LETTERS),
      id: 'w' + i,
    }))

    return {
      code: 'word_search',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Aşağıdaki kelimeleri tabloda bul ve işaretle.',
      render: [
        { t: 'text', value: placed.join(' · '), size: 'md', bold: true },
        { t: 'grid', cols: size, rows: size, cells, border: false },
      ],
      solution: { words: placed, placements },
      grading: 'auto',
      estimatedSeconds: placed.length * 35,
      itemCount: placed.length,
    }
  },
  /**
   * İki cevap biçimi kabul edilir:
   *
   *  - Kelime seçimi: `[{ cells: [...] }, ...]` — her öğe bir kelimenin
   *    harf dizisi. Ekranda çocuk ilk ve son harfe dokunur.
   *  - Düz hücre listesi: `['w3','w4',...]` — kağıttan aktarma.
   *
   * Birinci biçim, KESİŞEN harfler yüzünden gerekli: bir hücre iki
   * kelimeye birden ait olabilir ve hücreleri tek tek işaretleten bir
   * arayüzde ikinci kelimeyi seçmek birincisini bozuyordu.
   *
   * Doğrulamayı motor yapar, ekran değil: seçilen hücrelerin DÜZ BİR
   * ÇİZGİ oluşturduğu ve ızgaradaki harflerin listedeki bir kelimeyi
   * verdiği burada kontrol edilir.
   */
  grade(instance, answer) {
    const { words, placements } = instance.solution as {
      words: string[]
      placements: { word: string; cells: string[] }[]
    }
    const total = placements.length
    const grid = instance.render.find((n) => n.t === 'grid')
    const cols = grid && grid.t === 'grid' ? grid.cols : 0
    const letters =
      grid && grid.t === 'grid' ? grid.cells.map((c) => (c.glyph ?? '').toString()) : []

    const idx = (id: string) => Number(String(id).replace('w', ''))
    const fold = (s: string) =>
      s.toLocaleLowerCase('tr-TR')
        .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
        .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')
    const wanted = new Set(words.map(fold))

    const runs: string[][] = Array.isArray(answer)
      ? answer.every((a) => a && typeof a === 'object' && 'cells' in (a as object))
        ? (answer as { cells: string[] }[]).map((a) => a.cells)
        : [] // düz hücre listesi: aşağıdaki eski yola düşer
      : []

    if (runs.length > 0 && cols > 0) {
      const found = new Set<string>()
      for (const run of runs) {
        if (run.length < 2) continue
        const ids = run.map(idx)
        // Düz çizgi mi? Ham indeks farkına bakmak yetmez: yatay adım 1'dir
        // ama satır sonundan sonraki satıra atlayan bir dizi de 1 adımlıdır.
        // Satır ve sütun değişimi ayrı ayrı sabit olmalı.
        const dr0 = Math.floor(ids[1]! / cols) - Math.floor(ids[0]! / cols)
        const dc0 = (ids[1]! % cols) - (ids[0]! % cols)
        if (Math.abs(dr0) > 1 || Math.abs(dc0) > 1 || (dr0 === 0 && dc0 === 0)) continue
        const straight = ids.every((v, i) => {
          if (i === 0) return true
          const p = ids[i - 1]!
          return (
            Math.floor(v / cols) - Math.floor(p / cols) === dr0 && (v % cols) - (p % cols) === dc0
          )
        })
        if (!straight) continue
        const word = fold(ids.map((i) => letters[i] ?? '').join(''))
        if (wanted.has(word)) found.add(word)
      }
      const correct = found.size
      return {
        correct,
        total,
        accuracy: total ? correct / total : 0,
        perItem: words.map((w) => found.has(fold(w))),
        items: words.map((w) => ({
          id: w,
          beklenen: w,
          verilen: found.has(fold(w)) ? w : null,
          dogru: found.has(fold(w)),
        })),
      }
    }

    // Eski yol: hücreler tek tek işaretlenmiş
    const got = new Set((Array.isArray(answer) ? answer : []).map(String))
    const perItem = placements.map((p) => p.cells.every((c) => got.has(c)))
    const legit = new Set(placements.flatMap((p) => p.cells))
    const extra = [...got].filter((c) => !legit.has(c)).length
    const correct = Math.max(0, perItem.filter(Boolean).length - Math.floor(extra / 4))
    return {
      correct,
      total,
      accuracy: total ? correct / total : 0,
      perItem,
      items: placements.map((pl, i) => ({
        id: pl.word,
        beklenen: pl.word,
        verilen: perItem[i] ? pl.word : null,
        dogru: Boolean(perItem[i]),
      })),
    }
  },
}

/* ================================================================== *
 * 5. Yol takibi
 *
 * Karışık çizgilerden hangisi hangi harfe gidiyor. Kağıtta gözle,
 * ekranda parmakla izlenir.
 * ================================================================== */
export const pathTrace: ExerciseTemplate = {
  code: 'path_trace',
  title: 'Yolu takip et',
  domains: ['selective_attention', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { paths: scale(l, 3, 8), crossings: scale(l, 1, 6) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const count = p['paths'] as number

    const starts = LETTERS.slice(0, count)
    /**
     * Hedef sırası karıştırılır: A harfi 1'e gitmez. endSlot[i], i.
     * harfin SAĞDAKİ hangi satıra gittiğini söyler.
     */
    let endSlot = rng.shuffle(Array.from({ length: count }, (_, i) => i))
    // Hiçbir harf kendi sırasına gitmemeli. A→1, B→2 çıkarsa çocuk
    // çizgiyi takip etmeden doğru cevabı yazar ve egzersiz anlamını
    // kaybeder. Rastgele karıştırma bunu garanti etmez; zorluyoruz.
    let guard = 0
    while (count > 1 && endSlot.some((v, i) => v === i) && guard++ < 50) {
      endSlot = rng.shuffle(Array.from({ length: count }, (_, i) => i))
    }

    // Her yol: soldan sağa, aradan geçen rastgele kırılma noktalarıyla.
    // Kırılmalar olmasa çizgiler düz olur ve takip etmek gerekmezdi;
    // zorluk tam olarak bu kesişmelerden geliyor.
    const bends = 2 + Math.floor((clampLevel(level) * (p['crossings'] as number)) / 10)
    /**
     * Ayırt etme ipucu YALNIZCA ilk iki seviyede.
     *
     * Çizgileri ayırt edilebilir yapmak görevi kolaylaştırmaz,
     * DEĞİŞTİRİR: çocuk çizgiyi takip etmek yerine eşleştirir ve
     * ölçtüğümüz şey dikkat olmaktan çıkar. Küçük çocuğun göreve
     * ısınması için başlangıç seviyelerinde bir iskele olarak
     * bırakılıyor, sonra kalkıyor.
     *
     * İPUCU HEM RENKTE HEM ÇİZGİ BİÇİMİNDE. Önce yalnızca renkti ve
     * kâğıt kanalında sessizce kayboluyordu: ev yazıcısı çoğunlukla
     * siyah-beyaz basıyor, iskele gidiyor ve düzey 1 kâğıtta düzey 3
     * gibi oluyordu. İki kanal aynı bilgiyi taşıyor; biri kaybolsa
     * diğeri duruyor.
     *
     * Biçim sayısı sekiz değil dört: ilk iki seviyede en fazla dört
     * yol var (paths: scale(l, 3, 8)) ve sekiz ayırt edilebilir kesik
     * çizgi deseni zaten bulunmuyor.
     */
    const ipucu = clampLevel(level) <= 2
    const palette = ['#6aa9ff', '#f0834a', '#58c07a', '#c47ad9', '#e0c04a', '#4ac4c4', '#e06c8a', '#9aa7b8']
    const desenler = ['', '10 7', '2 6', '14 5 2 5']
    const items = starts.map((_, i) => {
      const y0 = (i + 0.5) / count
      const y1 = (endSlot[i]! + 0.5) / count
      const points: [number, number][] = [[0.1, y0]]
      for (let k = 1; k <= bends; k++) {
        const x = 0.1 + ((0.8 * k) / (bends + 1))
        const y = 0.06 + rng.float() * 0.88
        points.push([x, y])
      }
      points.push([0.9, y1])
      if (!ipucu) return { points, id: 'p' + i }
      const desen = desenler[i % desenler.length]!
      return {
        points,
        id: 'p' + i,
        color: palette[i % palette.length]!,
        // Düz çizgide dasharray yazmıyoruz: boş değer bazı
        // çizicilerde çizgiyi hiç göstermiyor
        ...(desen ? { dash: desen } : {}),
      }
    })

    const labels = [
      ...starts.map((s, i) => ({ x: 0.04, y: (i + 0.5) / count, text: s, id: 's' + i })),
      ...Array.from({ length: count }, (_, j) => ({
        x: 0.96,
        y: (j + 0.5) / count,
        text: String(j + 1),
        id: 'e' + j,
      })),
    ]

    return {
      code: 'path_trace',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Her harfin çizgisini takip et ve hangi sayıya gittiğini yaz.',
      render: [
        { t: 'paths', items, labels, height: Math.max(160, count * 36) },
        {
          t: 'rows',
          items: starts.map((s) => ({ label: `${s} →`, blank: true, id: 'ans' + s })),
          columns: 2,
        },
      ],
      solution: starts.map((_, i) => String(endSlot[i]! + 1)),
      grading: 'auto',
      estimatedSeconds: count * 20,
      itemCount: count,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}
